import { runtime } from "../core/runtime.js";
import { t, formatDate } from "../core/i18n.js";
import { dateKey, esc, tasksFor } from "../core/model.js";
import { monthDates, parseLocalDate, DAYS } from "../../shared/planning.js";
import { icon } from "./icons.js";
import { profile } from "../core/planning.js";
import { saveJSON } from "../core/storage.js";
import { KEYS } from "../../shared/schema.js";

export function currentTableMonth() {
  return dateKey(new Date()).slice(0, 7) + "-01";
}
export function selectedTableMonth() {
  const current = currentTableMonth();
  return runtime.tableMonth && runtime.tableMonth <= current
    ? runtime.tableMonth
    : current;
}
export function tableMonthDays() {
  const days = monthDates(parseLocalDate(selectedTableMonth()));
  // Retain yesterday inside the table when a new month starts.
  if (
    selectedTableMonth() === currentTableMonth() &&
    new Date().getDate() === 1
  ) {
    const yesterday = new Date(days[0]);
    yesterday.setDate(yesterday.getDate() - 1);
    days.unshift(yesterday);
  }
  return days;
}
export function resetTableView() {
  runtime.tableMonth = currentTableMonth();
  runtime.tableLayout = "all";
  runtime.tableSlice = currentTableSlice();
  const groups = tableGroups();
  const preferred = runtime.state.prefs?.tableGroup;
  runtime.tableGroup = groups.some((group) => group.id === preferred)
    ? preferred
    : groups[0].id;
  runtime.tableScrollTarget = "recent";
  runtime.tableScrollTop = 0;
  runtime.tableScrollLeft = 0;
}
export function tableGroups() {
  return profile().groups.map((group) => ({
    ...group,
    label:
      group.name ||
      t(
        group.id === "Weekdays"
          ? "Mon–Fri"
          : group.id === "Weekend"
            ? "Sat–Sun"
            : "Every day",
      ),
    detail: group.days.map((index) => t(DAYS[index])).join(" · "),
  }));
}

export function currentTableSlice() {
  return selectedTableMonth() === currentTableMonth()
    ? Math.min(3, Math.floor((new Date().getDate() - 1) / 7))
    : 0;
}

function tableContext(days) {
  const month = selectedTableMonth();
  const groups = tableGroups();
  const group =
    groups.find((item) => item.id === runtime.tableGroup) || groups[0];
  const groupDays = days.filter((day) =>
    group.days.includes((day.getDay() + 6) % 7),
  );
  const scheduled = new Map(
    groupDays.map((day) => [dateKey(day), tasksFor(day)]),
  );
  const identities = new Map(
    [...scheduled].map(([key, tasks]) => [
      key,
      new Set(tasks.map((task) => task.id + "\0" + task.name)),
    ]),
  );
  const tasks = [
    ...new Map(
      [...scheduled.values()]
        .flat()
        .map((task) => [task.id + "\0" + task.name, task]),
    ).values(),
  ];
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const calendar = days.filter(
    (day) => dateKey(day).slice(0, 7) === month.slice(0, 7),
  );
  const periods = [
    calendar.slice(0, 7),
    calendar.slice(7, 14),
    calendar.slice(14, 21),
    calendar.slice(21),
  ];
  return {
    month,
    label: formatDate(parseLocalDate(month), {
      month: "long",
      year: "numeric",
    }),
    groups,
    group,
    scheduled,
    identities,
    tasks,
    today: dateKey(new Date()),
    yesterday: dateKey(yesterday),
    days: groupDays,
    periods,
  };
}

function dateKind(key, context) {
  return key === context.today
    ? "today"
    : key === context.yesterday
      ? "yesterday"
      : "";
}

function dayProgress(key, context) {
  const scheduled = context.scheduled.get(key) || [];
  if (!scheduled.length || key > context.today) return "";
  const done = scheduled.filter(
    (task) => !!runtime.state.completions[key]?.[task.id],
  ).length;
  return `<span data-day-progress data-date-progress="${key}" aria-live="polite">${done}/${scheduled.length}</span>`;
}

function badge(kind) {
  return kind
    ? `<span class="month-day-badge">${t(kind === "today" ? "Today" : "Yesterday")}</span>`
    : "";
}

function planCell(day, task, context, renderCell) {
  const key = dateKey(day);
  return context.identities.get(key)?.has(task.id + "\0" + task.name)
    ? renderCell(key, task)
    : `<td class="sheet-cell unscheduled" aria-label="${esc(task.name)} — ${key}: ${t("Not scheduled")}">—</td>`;
}

function renderFullGrid(context, renderCell) {
  const { tasks } = context;
  const headers = tasks.length
    ? tasks
        .map(
          (task, index) =>
            `<th scope="col" title="${esc(task.name)}"><span class="month-task-number">${String(index + 1).padStart(2, "0")}</span><span class="month-task-name">${esc(task.name)}</span>${task.schedule ? `<small>${esc(task.schedule)}</small>` : ""}</th>`,
        )
        .join("")
    : `<th scope="col">${t("Plan")}</th>`;
  const rows = context.days
    .map((day) => {
      const key = dateKey(day),
        kind = dateKind(key, context),
        progress = dayProgress(key, context);
      return `<tr data-month-date="${key}" class="month-row-${kind || (key > context.today ? "future" : "past")}"><th scope="row" class="month-date-cell"><span class="month-date-main">${formatDate(day, { day: "numeric", month: "short" })}${badge(kind)}</span><small>${formatDate(day, { weekday: "short" })}${progress ? ` · ${progress}` : ""}</small></th>${tasks.length ? tasks.map((task) => planCell(day, task, context, renderCell)).join("") : `<td class="month-no-plans">${t("No plans for this day")}</td>`}</tr>`;
    })
    .join("");
  return `<div class="month-sheet-scroll" tabindex="0" role="region" aria-label="${esc(context.label)} — ${t("Full table")}"><table class="month-sheet-table" style="--month-columns:${Math.max(1, tasks.length)}"><thead><tr><th scope="col" class="month-date-cell">${t("Date")}</th>${headers}</tr></thead><tbody>${rows}</tbody></table><div class="month-sheet-end-space" aria-hidden="true"></div></div>`;
}

function renderWeekGrid(context, renderCell) {
  const index = Math.max(0, Math.min(3, runtime.tableSlice));
  const visible = context.periods[index].filter((day) =>
    context.scheduled.has(dateKey(day)),
  );
  if (
    visible.some((day) => dateKey(day) === context.today) &&
    !visible.some((day) => dateKey(day) === context.yesterday)
  ) {
    const yesterday = context.days.find(
      (day) => dateKey(day) === context.yesterday,
    );
    if (yesterday) visible.unshift(yesterday);
  }
  const headers = visible
    .map((day) => {
      const key = dateKey(day),
        kind = dateKind(key, context);
      return `<th scope="col" data-week-date="${key}" class="month-week-date month-week-${kind || "day"}"><span>${formatDate(day, { weekday: "short" })}</span><b>${formatDate(day, { day: "numeric", month: "short" })}</b>${badge(kind)}<small>${dayProgress(key, context)}</small></th>`;
    })
    .join("");
  const rows = context.tasks.length
    ? context.tasks
        .map(
          (task, index) =>
            `<tr><th scope="row" class="month-plan-cell"><span class="month-task-number">${String(index + 1).padStart(2, "0")}</span><span class="month-task-name">${esc(task.name)}</span>${task.schedule ? `<small>${esc(task.schedule)}</small>` : ""}</th>${visible.map((day) => planCell(day, task, context, renderCell)).join("")}</tr>`,
        )
        .join("")
    : `<tr><th scope="row" class="month-plan-cell">${t("Plan")}</th><td class="month-no-plans" colspan="${visible.length}">${t("No plans for this day")}</td></tr>`;
  return `<div class="month-period-toolbar"><div class="month-periods" role="group" aria-label="${t("Days of the month")}">${context.periods.map((period, position) => `<button type="button" data-table-slice="${position}" aria-pressed="${index === position}">${period[0].getDate()}–${period.at(-1).getDate()}</button>`).join("")}</div></div><div class="month-sheet-scroll" tabindex="0" role="region" aria-label="${esc(context.label)} — ${t("Week view")}"><table class="month-sheet-table month-week-table" style="--week-days:${visible.length}"><thead><tr><th scope="col" class="month-plan-cell">${t("Plan")}</th>${headers}</tr></thead><tbody>${rows}</tbody></table><div class="month-sheet-end-space" aria-hidden="true"></div></div>`;
}

export function renderMonthlyPlanTable(days, renderCell) {
  const context = tableContext(days),
    week = runtime.tableLayout === "week";
  const groups = `<div class="month-group-tabs" role="group" aria-label="${t("Choose plan group")}">${context.groups.map((group) => `<button type="button" data-table-group="${esc(group.id)}" aria-pressed="${group.id === context.group.id}" title="${esc(group.detail)}">${esc(group.label)}</button>`).join("")}</div>`;
  const layouts = `<div class="month-layout-tabs" role="group" aria-label="${t("Plan overview")}"><button type="button" data-table-layout="week" aria-pressed="${week}">${icon("weekly")}${t("Week view")}</button><button type="button" data-table-layout="all" aria-pressed="${!week}">${icon("table")}${t("Full table")}</button></div>`;
  return `<section class="month-sheet" aria-labelledby="monthTableTitle"><header class="month-sheet-toolbar"><div class="month-sheet-title">${icon("table")}<h1 id="monthTableTitle">${esc(context.label)}</h1></div><div class="month-sheet-controls"><div class="month-navigation" role="group" aria-label="${t("Choose month")}"><button type="button" data-month-shift="-1" aria-label="${t("Previous month")}" title="${t("Previous month")}">${icon("arrow")}</button><button type="button" data-month-shift="1" aria-label="${t("Next month")}" title="${t("Next month")}" ${context.month >= currentTableMonth() ? "disabled" : ""}>${icon("arrow")}</button></div><button type="button" class="month-jump" data-table-jump="start">${t("Month start")}</button><button type="button" class="month-jump month-jump-current" data-table-jump="recent">${t("Yesterday & today")}</button></div></header><div class="month-options-toolbar">${groups}${layouts}</div>${week ? renderWeekGrid(context, renderCell) : renderFullGrid(context, renderCell)}</section>`;
}

export function scrollMonthTable(target, behavior = "auto") {
  const scroll = document.querySelector(".month-sheet-scroll");
  if (!scroll) return;
  const header = scroll.querySelector("thead");
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const rows = [...scroll.querySelectorAll("[data-month-date]")];
  const row =
    rows.find((item) => item.dataset.monthDate === dateKey(yesterday)) ||
    rows.find((item) => item.dataset.monthDate === dateKey(new Date())) ||
    rows.findLast((item) => item.dataset.monthDate < dateKey(new Date()));
  const spacer = scroll.querySelector(".month-sheet-end-space");
  const top =
    target === "recent" && row
      ? Math.max(
          0,
          row.getBoundingClientRect().top -
            scroll.getBoundingClientRect().top +
            scroll.scrollTop -
            header.offsetHeight -
            scroll.clientTop,
        )
      : 0;
  spacer.style.height = "0px";
  spacer.style.height = `${Math.max(0, top + scroll.clientHeight - scroll.scrollHeight)}px`;
  scroll.scrollTo({ top, left: 0, behavior });
}
export function mountMonthlyTable() {
  const scroll = document.querySelector(".month-sheet-scroll");
  if (!scroll) return;
  requestAnimationFrame(() => {
    if (!scroll.isConnected) return;
    if (runtime.tableScrollTarget) {
      window.scrollTo({ top: 0, behavior: "instant" });
      scrollMonthTable(runtime.tableScrollTarget);
    } else {
      const top = runtime.tableScrollTop,
        left = runtime.tableScrollLeft;
      scrollMonthTable("recent");
      scroll.scrollTo({
        top,
        left,
      });
    }
    runtime.tableScrollTarget = null;
    scroll.addEventListener(
      "scroll",
      () => {
        if (!scroll.isConnected) return;
        runtime.tableScrollTop = scroll.scrollTop;
        runtime.tableScrollLeft = scroll.scrollLeft;
      },
      { passive: true },
    );
  });
}
export function bindMonthlyTable(render) {
  document.querySelectorAll("[data-table-layout]").forEach(
    (button) =>
      (button.onclick = () => {
        runtime.tableLayout =
          button.dataset.tableLayout === "week" ? "week" : "all";
        if (runtime.tableLayout === "week")
          runtime.tableSlice = currentTableSlice();
        runtime.tableScrollTarget = "recent";
        render();
        document
          .querySelector('[data-table-layout="' + runtime.tableLayout + '"]')
          ?.focus({ preventScroll: true });
      }),
  );
  document.querySelectorAll("[data-table-group]").forEach(
    (button) =>
      (button.onclick = () => {
        if (
          !tableGroups().some((group) => group.id === button.dataset.tableGroup)
        )
          return;
        runtime.tableGroup = button.dataset.tableGroup;
        runtime.state.prefs.tableGroup = runtime.tableGroup;
        saveJSON(KEYS.prefs, runtime.state.prefs);
        runtime.tableScrollTarget =
          selectedTableMonth() === currentTableMonth() ? "recent" : "start";
        render();
        document
          .querySelector('[data-table-group="' + runtime.tableGroup + '"]')
          ?.focus({ preventScroll: true });
      }),
  );
  document.querySelectorAll("[data-table-slice]").forEach(
    (button) =>
      (button.onclick = () => {
        const index = Number(button.dataset.tableSlice);
        if (!Number.isInteger(index) || index < 0 || index > 3) return;
        runtime.tableSlice = index;
        runtime.tableScrollTarget = "start";
        render();
        document
          .querySelector('[data-table-slice="' + index + '"]')
          ?.focus({ preventScroll: true });
      }),
  );
  document.querySelectorAll("[data-month-shift]").forEach(
    (button) =>
      (button.onclick = () => {
        const month = parseLocalDate(selectedTableMonth());
        month.setMonth(month.getMonth() + Number(button.dataset.monthShift));
        if (dateKey(month) > currentTableMonth()) return;
        runtime.tableMonth = dateKey(month);
        runtime.tableSlice = currentTableSlice();
        runtime.tableScrollTarget =
          runtime.tableMonth === currentTableMonth() ? "recent" : "start";
        runtime.tableScrollLeft = 0;
        render();
        const control = document.querySelector(
          `[data-month-shift="${button.dataset.monthShift}"]`,
        );
        (control?.disabled
          ? document.querySelector('[data-table-jump="recent"]')
          : control
        )?.focus({ preventScroll: true });
      }),
  );
  document.querySelectorAll("[data-table-jump]").forEach(
    (button) =>
      (button.onclick = () => {
        if (
          button.dataset.tableJump === "recent" &&
          selectedTableMonth() !== currentTableMonth()
        ) {
          resetTableView();
          render();
          document
            .querySelector('[data-table-jump="recent"]')
            ?.focus({ preventScroll: true });
        } else if (
          button.dataset.tableJump === "recent" &&
          runtime.tableLayout === "week"
        ) {
          runtime.tableSlice = currentTableSlice();
          runtime.tableScrollTarget = "start";
          render();
          document
            .querySelector('[data-table-jump="recent"]')
            ?.focus({ preventScroll: true });
        } else
          scrollMonthTable(
            button.dataset.tableJump,
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
              ? "auto"
              : "smooth",
          );
      }),
  );
}
